/**
 * 4 - Capture an Enemy Officer (03 §2): an HQ of 3-5 shelters (tents; guard houses where the
 * theatre has no tents, 03 §2.1), the officer at the flap of one of them - and THAT shelter is
 * what the strip map marks (ROUND5 §2.1). On capture the pickup moves to him; at alert 3 he has
 * 60 s before he flees north.
 */

import type { PropDef } from '../../sim/types';
import type { Point } from '../types';
import { checker, clutter, guardAtDoor, guardsAbout, insideRect, required, ruleOf, structure } from './helpers';
import { flagTargets } from './helpers3b';
import type { ArchetypeSpec } from './types';

export const captureOfficer: ArchetypeSpec = {
  id: 'capture-officer',
  name: 'CAPTURE AN ENEMY OFFICER',
  nativeBiome: 'desert',
  baseThreat: 3,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [46, 56], d: [28, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    const count = 3 + g.rng.int(3);
    const shelters: Array<PropDef | null> = [];
    for (let i = 0; i < count; i++) {
      const o = { minDist: 8, from: placed, margin: 1.6 };
      shelters.push(i < 3 ? required(g, g.kit.shelter, o) : structure(g, g.kit.shelter, o));
    }
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 4);

    const standing = shelters.filter((s): s is PropDef => s !== null && s.id >= 0);
    const his = standing[g.rng.int(Math.max(1, standing.length))];
    const officer = his ? guardAtDoor(g, his, 'officer', { captureTarget: true }) : null;
    if (!officer) g.map.problems.push('capture-officer: nowhere to stand the officer');
    // His staff sleep elsewhere: a guard right beside him would make the capture impossible (02 §9.1).
    for (const s of standing) if (s !== his && g.rng.chance(0.6)) guardAtDoor(g, s);
    guardsAbout(g, 1 + g.rng.int(2));
    // ROUND5 §2.1 (the marker audit): "THE OFFICER IS IN ONE OF THEM" - and the map marked nothing at all,
    // the only mission in the game with no mark of any kind. His tent is the proxy, exactly as the laboratory
    // is the scientist's (mission 30) and the ruin the pilot's (27). Flagged in `finish`, not here: a prop
    // flagged during the build gets a sentry post of its own (posts.ts), and a guard at his elbow would make
    // the capture impossible (02 §9.1).
    const markerId = his?.id ?? -1;
    return {
      objective: {
        kind: 'captureAndEscort', label: 'CAPTURE OFFICER', targetPropIds: [markerId], minFraction: 1,
        rules: [
          { type: 'recall', mode: 'relocatesToCapture' },
          { type: 'alertConsequence', level: 3, delaySeconds: 60, effect: 'officerFlees' },
        ],
      },
      finish(def) {
        flagTargets(def, [markerId]);
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('capture-officer');
    const officers = def.spawns.filter((s) => s.captureTarget === true);
    check(officers.length === 1 && officers[0]?.kind === 'officer', 'needs exactly one officer flagged captureTarget');
    const shelter = (kind: string): boolean => kind === 'tent' || kind === 'guardHouse';
    const hq = def.props.filter((p) => shelter(p.kind) && insideRect(def.goalZone, p.x, p.z));
    check(hq.length >= 3 && hq.length <= 5, `the HQ needs 3-5 tents, has ${hq.length}`);
    check(def.objective.kind === 'captureAndEscort', 'objective must be captureAndEscort');
    // ROUND5: the tent he is at is the one marker, and it is the tent he is really at.
    const marker = def.props.find((p) => p.id === def.objective.targetPropIds[0]);
    check(def.objective.targetPropIds.length === 1, 'his shelter is the one marker the mission carries');
    check(
      marker !== undefined && shelter(marker.kind) && marker.target === true,
      'the shelter the officer is at must be marked on the strip map',
    );
    const [officer] = officers;
    if (marker && officer) {
      check(hq.includes(marker), 'the marked shelter must be one of the HQ shelters');
      check(
        Math.abs(marker.x - officer.x) <= 7 && Math.abs(marker.z - officer.z) <= 7,
        'the marked shelter must be the one the officer stands at',
      );
      check(probe.breach(marker.id) === 0, 'the HQ must be reachable through the open gates');
    }
    check(ruleOf(def, 'recall')?.mode === 'relocatesToCapture', 'the pickup must relocate to the capture');
    const flees = ruleOf(def, 'alertConsequence');
    check(
      flees?.effect === 'officerFlees' && flees.level === 3 && flees.delaySeconds === 60,
      'the officer must flee 60 s after alert 3',
    );
    return out;
  },
};
